<template>
    <section
        v-if="candidateProfile && !loading"
        class="candidate-profile-card card mb-3 p-component"
        :class="{ editing: profileEditable }"
        aria-labelledby="candidateDetails"
    >
        <div class="card-header d-flex justify-content-between">
            <h1 class="candidate-name my-1" id="candidateDetails">
                {{ candidateFullName }}
            </h1>
            <div class="card-actions">
                <button
                    class="card-action save-action btn btn-light me-2"
                    v-if="profileEditable"
                    @click="saveProfile"
                >
                    <font-awesome-icon icon="far fa-save" class="fs-5 btn-icon"/>
                    <span class="btn-label">
                        {{ $t('btn_save') }}
                    </span>
                </button>
                <button
                    class="card-action toggle-profile-edit-action btn btn-light px-0"
                    @click="editProfileToggle"
                >
                    <span v-if="profileEditable">
                        <font-awesome-icon icon="fas fa-undo" class="btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_cancel') }}
                        </span>
                    </span>
                    <span v-else>
                        <font-awesome-icon icon="fas fa-pencil" class="btn-icon"/>
                        <span class="btn-label">
                            {{ $t('btn_edit') }}
                        </span>
                    </span>
                </button>
            </div>
        </div>

        <form
            v-if="profileEditable"
            ref="personalInfoForm"
            @submit.prevent="updateProfile"
            class="card-body"
        >
            <div class="candidate-details">
                <div class="row">
                    <div class="form-group col-sm-4">
                        <label for="firstName" class="form-label" id="firstNameLabel">
                            {{ $t('lib_firstname') }}
                        </label>
                        <input
                            id="firstName"
                            disabled
                            type="text"
                            :value="candidate.usualFirstName"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage('usualFirstName'),
                            }"
                            aria-labelledby="firstNameLabel"
                        />
                        <div
                            v-if="errorMessage('usualFirstName')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('usualFirstName') }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="lastName" class="form-label" id="lastNameLabel">
                            {{ $t('lib_lastname') }}
                        </label>
                        <input
                            id="lastName"
                            disabled
                            type="text"
                            :value="candidate.usualLastName"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage('usualLastName'),
                            }"
                            aria-labelledby="lastNameLabel"
                        />
                        <div
                            v-if="errorMessage('usualLastName')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('usualLastName') }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="email" class="form-label" id="emailLabel">
                            {{ $t('lib_email') }}
                        </label>
                        <input
                            type="text"
                            :value="candidate.email"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage('email'),
                            }"
                            id="email"
                            disabled
                            aria-labelledby="emailLabel"
                        />
                        <div
                            v-if="errorMessage('email')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('email') }}
                        </div>
                    </div>
                    <div
                        class="form-group col-sm-4"
                        :class="{
                            'required-group': isFieldRequired('birthDate'),
                        }"
                    >
                        <label for="birthDate" class="form-label" id="birthDateLabel">
                            {{ $t('lib_birth_date') }}
                        </label>
                        <Calendar
                            v-model="candidate.birthDate"
                            :minDate="minDateValue"
                            :maxDate="maxDateValue"
                            :showIcon="true"
                            :class="{
                                'is-invalid': errorMessage('birthDate'),
                            }"
                            required
                            id="birthDate"
                            aria-labelledby="birthDateLabel"
                        />
                        <div
                            v-if="errorMessage('birthDate')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('birthDate') }}
                        </div>
                    </div>
                    <div
                        class="form-group col-sm-4"
                        :class="{
                            'required-group': isFieldRequired('birthCity'),
                        }"
                    >
                        <label for="birthCity" class="form-label" id="birthCityLabel">
                            {{ $t('lib_birth_place') }}
                        </label>
                        <input
                            id="birthCity"
                            required
                            type="text"
                            v-model="candidate.birthCity"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage('birthCity'),
                            }"
                            aria-labelledby="birthCityLabel"
                        />
                        <div
                            v-if="errorMessage('birthCity')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('birthCity') }}
                        </div>
                    </div>
                    <div
                        class="form-group col-sm-4"
                        :class="{
                            'required-group': isFieldRequired(
                                'birthIsoCountryCode'
                            ),
                        }"
                    >
                        <label for="birthCountry" class="form-label" id="birthCountryLabel">
                            {{ $t('lib_birth_country') }}
                        </label>
                        <Dropdown
                            id="birthCountry"
                            required
                            v-model="candidate.birthIsoCountryCode"
                            :class="{
                                'is-invalid': errorMessage(
                                    'birthIsoCountryCode'
                                ),
                            }"
                            :filter="true"
                            :showClear="true"
                            :options="countries"
                            optionLabel="name"
                            optionValue="code"
                            :placeholder="$t('cand_choose')"
                            aria-labelledby="birthCountryLabel"
                        />
                        <div
                            v-if="errorMessage('birthIsoCountryCode')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('birthIsoCountryCode') }}
                        </div>
                    </div>
                    <div
                        class="form-group col-sm-4"
                        :class="{
                            'required-group':
                                isFieldRequired('natIsoCountryCode'),
                        }"
                    >
                        <label for="nationality" class="form-label" id="nationalityLabel">
                            {{ $t('lib_nationality') }}
                        </label>
                        <Dropdown
                            id="nationality"
                            v-model="candidate.natIsoCountryCode"
                            :options="natIsoCountries"
                            optionLabel="name"
                            optionValue="code"
                            :filter="true"
                            :showClear="true"
                            :placeholder="$t('cand_choose')"
                            :class="{
                                'is-invalid': errorMessage('natIsoCountryCode'),
                            }"
                            required
                            aria-labelledby="nationalityLabel"
                        >
                        </Dropdown>
                        <div
                            v-if="errorMessage('natIsoCountryCode')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('natIsoCountryCode') }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="secondnationality" class="form-label" id="secondNationalityLabel">
                            {{ $t('lib_nationality2') }}
                        </label>
                        <Dropdown
                            id="secondnationality"
                            v-model="candidate.nat2IsoCountryCode"
                            :options="nat2IsoCountries"
                            optionLabel="name"
                            optionValue="code"
                            :filter="true"
                            :placeholder="$t('cand_choose')"
                            :showClear="true"
                            :class="{
                                'is-invalid':
                                    errorMessage('nat2IsoCountryCode'),
                            }"
                            aria-labelledby="secondNationalityLabel"
                        >
                        </Dropdown>
                        <div
                            v-if="errorMessage('nat2IsoCountryCode')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('nat2IsoCountryCode') }}
                        </div>
                    </div>
                    <div
                        v-if="genders"
                        class="form-group col-sm-4"
                        :class="{
                            'required-group': isFieldRequired('gender'),
                        }"
                    >
                        <label for="gender" class="form-label" id="candidateGenderLabel">
                            {{ $t('lib_gender') }}
                        </label>
                        <Dropdown
                            id="gender"
                            v-model="candidate.gender"
                            :class="{
                                'is-invalid': errorMessage('gender'),
                            }"
                            :options="genders"
                            optionLabel="name"
                            optionValue="code"
                            :placeholder="$t('cand_choose')"
                            :required="true"
                            aria-labelledby="candidateGenderLabel"
                        />
                        <div
                            v-if="errorMessage('gender')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('gender') }}
                        </div>
                    </div>
                    <div
                        v-if="candidate.gender === 'OTHER'"
                        class="form-group col-sm-4"
                    >
                        <label for="identifyAs" class="form-label" id="identifyAsLabel">
                            {{ $t('lib_pref_pronouns') }} 
                        </label>
                        <input
                            type="text"
                            v-model="candidate.identifyAs"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage('identifyAs'),
                            }"                           
                            id="identifyAs"
                            aria-labelledby="identifyAsLabel"
                        />
                        <div
                            v-if="errorMessage('identifyAs')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('identifyAs') }}
                        </div>
                    </div>
                    <div
                         class="form-group col-sm-4"
                         :class="{
                            'required-group': isFieldRequired('phoneNumber'),
                        }"
                         >
                        <label for="telephone" class="form-label" id="telephoneLabel">
                            {{ $t('lib_phone') }}
                        </label>
                        <InputMask
                                   mask="+99 99999999999"
                                   :unmask="true"
                                   :autoClear="false"
                                   slotChar=""
                                   id="telephone"
                                   required
                                   class="form-control"
                                   :class="{
                                'is-invalid': errorMessage('phoneNumber'),
                            }"
                                   v-model="candidate.phoneNumber"
                                   min="12"
                                   max="20"
                                   aria-labelledby="telephoneLabel"
                                   />
                        <small>
                            Eg: +32 99999999.
                        </small>
                        <div 
                             v-if="errorMessage('phoneNumber')"
                             class="invalid-feedback"
                             >                            
                            {{ errorMessage('phoneNumber') }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="gsm" class="form-label" id="secondPhoneLabel">
                            {{ $t('lib_phone2') }}
                        </label>
                        <InputMask
                                   mask="+99 99999999999"
                                   :unmask="true"
                                   :autoClear="false"
                                   slotChar=""
                                   id="gsm"
                                   class="form-control"
                                   :class="{
                                'is-invalid': errorMessage('gsmNumber'),
                            }"
                                   v-model="candidate.gsmNumber"
                                   min="12"
                                   max="20"
                                   aria-labelledby="secondPhoneLabel" 
                                   />
                        <small
                               >Eg: +32 99999999.
                        </small>
                        <div 
                             v-if="errorMessage('gsmNumber')"
                             class="invalid-feedback"
                             >
                            {{ errorMessage('gsmNumber') }}
                        </div>
                    </div>
                    <div class="form-group col-sm-4">
                        <label for="secondaryEmail" class="form-label" id="secondaryEmailLabel">
                            {{ $t('lib_secondary_email') }}
                        </label>
                        <input
                            type="email"
                            v-model="candidate.secondaryEmail"
                            class="form-control"
                            :class="{
                                'is-invalid': errorMessage('secondaryEmail'),
                            }"
                            id="secondaryEmail"
                            aria-labelledby="secondaryEmailLabel"
                        />
                        <div
                            v-if="errorMessage('secondaryEmail')"
                            class="invalid-feedback"
                        >
                            {{ errorMessage('secondaryEmail') }}
                        </div>
                    </div>
                </div>
            </div>
            <button
                type="submit"
                ref="personalInfoFormSubmit"
                class="submit-candididate-profile-btn btn btn-success d-none"
            >
                <!-- Added this button to have the html form validation on inputs -->
                Submit
            </button>
        </form>
    </section>
    <div v-if="loading" class="card candidate-profile-skeleton mb-5">
        <div class="card-header d-flex justify-content-between py-3">
            <Skeleton width="14rem"></Skeleton>
            <Skeleton width="2rem"></Skeleton>
        </div>
        <div class="card-body d-flex justify-content-between flex-wrap">
            <div>
                <Skeleton width="10rem" class="mb-2"></Skeleton>
                <Skeleton width="5rem" class="mb-2"></Skeleton>
                <Skeleton width="7rem" class="mb-2"></Skeleton>
            </div>
            <div>
                <Skeleton width="10rem" class="ms-md-auto mb-2"></Skeleton>
                <Skeleton width="8rem" class="ms-md-auto mb-2"></Skeleton>
                <Skeleton width="5rem" class="ms-md-auto mb-2"></Skeleton>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Calendar from 'primevue/calendar';
import InputMask from 'primevue/inputmask';
import Dropdown from 'primevue/dropdown';
import Skeleton from 'primevue/skeleton';
import CommonMixins from '../mixins';

export default {
    name: 'CandidateProfileCard',
    components: {
        Calendar,
        InputMask,
        Dropdown,
        Skeleton,
    },
    props: {
        showHeader: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            profileEditable: false,
            candidate: null,
            loading: true,
            formWasChanged: false,
        };
    },
    computed: {
        ...mapGetters([
            'genders',
            'countries',
            'isCORSite',
            'selectedLanguage',
            'candidateProfile',
            'candidateProfileErrors',
            'minDateValue',
            'maxDateValue',
            'isFieldRequired',
            'countryLabel',
        ]),
        candidateFullName() {
            return (
                this.candidateProfile.usualFirstName +
                ' ' +
                this.candidateProfile.usualLastName
            );
        },
        natIsoCountries() {
            // Nationality should be different of Second Nationality;
            return this.countries.filter(
                (country) => country.code !== this.candidate.nat2IsoCountryCode
            );
        },
        nat2IsoCountries() {
            // Second Nationality should be different of Nationality;
            return this.countries.filter(
                (country) => country.code !== this.candidate.natIsoCountryCode
            );
        },
    },
    methods: {
        ...mapActions([
            'updateCandidateProfile',
            'loadCandidateProfile',
            'clearCandidateProfileErrors',
        ]),
        editProfileToggle() {
            this.candidate = {
                ...this.candidateProfile,
                birthDate: this.candidateProfile.birthDate
                    ? new Date(this.candidateProfile.birthDate)
                    : this.candidateProfile.birthDate,
            };
            this.profileEditable = !this.profileEditable;
            this.clearCandidateProfileErrors();
        },
        saveProfile() {
            this.$refs.personalInfoFormSubmit.click();
        },
        updateProfile() {
            if (this.formWasChanged) {
                this.loading = true;
                this.updateCandidateProfile(this.candidate).then(
                    () => {
                        this.loading = false;
                        this.editProfileToggle();
                        this.$toast.add({
                            severity: 'success',
                            detail: 'Profile updated succesfully!',
                            life: 3000,
                        });
                    },
                    (error) => {
                        this.loading = false;
                        this.$toast.removeAllGroups();
                        this.$toast.add({
                            severity: 'error',
                            detail: error.title,
                            life: 3000,
                        });
                    }
                );
            } else {
                this.editProfileToggle();
            }
        },
        errorMessage(key) {
            return this.candidateProfileErrors[key]
                ? this.$t(this.candidateProfileErrors[key])
                : false;
        },
    },

    mounted() {
        this.loadCandidateProfile().then(() => {
            this.loading = false;
        });
    },
    watch: {
        // whenever candidate object changes, this function will run and this way we will now if the form data was changed so we can save changes;
        candidate: {
            handler(newData) {
                let storeData = {
                    ...this.candidateProfile,
                    birthDate: this.candidateProfile.birthDate
                        ? new Date(this.candidateProfile.birthDate)
                        : this.candidateProfile.birthDate,
                };
                this.formWasChanged = !(
                    JSON.stringify(newData) === JSON.stringify(storeData)
                );
            },
            deep: true,
        },
    },
    mixins: [CommonMixins],
};
</script>
